// @section 1 MyDay route — V14 spine (per _docs/optiq-hq/UI-TEMPLATE-V14.md)
// Breadcrumb · KPIs-beside-title · alert cards · hour-grid w/ gaps · capture box.
// Wired to live Bridge calendar + Gmail + hq_* aggregate.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../components/Toast';
import {
  getMyDay,
  listMyCalendars,
  listUnifiedCalendarEvents,
  listGmailMessages,
  getGmailMessage,
  seedDayBlocks,
  transitionTask,
  azToday,
  isFramedBlock,
  type CalEvent,
  type WorkspaceCalendar,
} from '../api';
import { useMeUser } from '../lib/useMe';
import { useIsMobile } from '../lib/useIsMobile';

type MyTask = {
  id: string;
  my_day_rank?: number;
  urgency?: string;
  title: string;
  body?: string | null;
  status: string;
  priority: number;
  due_date?: string | null;
  parent_type: string;
  parent_id?: string | null;
  sort_order: number;
};

type GmailRow = { id: string; from?: string; subject?: string; date?: string; snippet?: string; ageDays: number };
type Severity = 'stall' | 'watch' | 'ok';
type Alert = {
  key: string;
  sev: Severity;
  ticker: string;
  eyebrow: string;
  headline: string;
  detail: string;
  to?: string;
  primary: string;
};

const GRID_START_H = 7; // 7:00 AM
const GRID_END_H = 19; // 7:00 PM

// ── Alert dismissal store (localStorage; per-browser until a backend table lands)
const DISMISS_KEY = 'atlas.alertDismiss.v1';
const RESOLVED_UNTIL = 8.64e15; // max timestamp — effectively permanent
type DismissMap = Record<string, number>; // alertKey -> suppress-until epoch ms
function loadDismiss(): DismissMap {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}'); } catch { return {}; }
}
function saveDismiss(m: DismissMap) {
  try { localStorage.setItem(DISMISS_KEY, JSON.stringify(m)); } catch { /* ignore */ }
}

export default function MyDay() {
  const nav = useNavigate();
  const me = useMeUser();
  const subject = me?.workspaceSubject ?? null;
  const [hq, setHq] = useState<any | null>(null);
  const [cals, setCals] = useState<WorkspaceCalendar[] | null>(null);
  const [events, setEvents] = useState<CalEvent[] | null>(null);
  const [emails, setEmails] = useState<GmailRow[] | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [dismissTick, setDismissTick] = useState(0);
  const [seeding, setSeeding] = useState(false);
  const now = new Date();

  // Load TODAY across every unified calendar (Adam's 5 accounts; just one for
  // everyone else). listUnifiedCalendarEvents tags each event with its source
  // account and de-dupes meetings that sit on more than one of his calendars.
  const loadEvents = useCallback(() => {
    if (!cals || cals.length === 0) return Promise.resolve();
    const start = new Date(); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + 1);
    return listUnifiedCalendarEvents(cals, start.toISOString(), end.toISOString(), 40)
      .then((r) => {
        setEvents(r.items || []);
        setErrs((p) => {
          const next = { ...p };
          if (r.errors.length) next.events = `${r.errors.length} calendar${r.errors.length === 1 ? '' : 's'} unavailable right now`;
          else delete next.events;
          return next;
        });
      })
      .catch((e) => setErrs((p) => ({ ...p, events: String(e) })));
  }, [cals]);

  // Wait for the signed-in user to resolve, then load their aggregate, the set of
  // calendars to unify, and their inbox. The timeline itself loads in the effect
  // below, keyed on the resolved calendar set.
  useEffect(() => {
    if (!me) return;
    getMyDay(me.userId).then(setHq).catch((e) => setErrs((p) => ({ ...p, hq: String(e) })));
    listMyCalendars(me).then(setCals).catch((e) => setErrs((p) => ({ ...p, events: String(e) })));

    listGmailMessages(me.workspaceSubject, ['INBOX', 'UNREAD'], 12)
      .then(async (r) => {
        const ids = (r.messages || []).map((m) => m.id);
        const rows = await Promise.all(ids.map(async (id) => {
          try {
            const m = await getGmailMessage(me.workspaceSubject, id);
            const h = m.payload?.headers ?? [];
            const get = (n: string) => h.find((x) => x.name.toLowerCase() === n.toLowerCase())?.value;
            const dateStr = get('Date');
            const ageDays = dateStr ? (Date.now() - new Date(dateStr).getTime()) / 86_400_000 : 0;
            return { id, from: get('From'), subject: get('Subject'), date: dateStr, snippet: m.snippet, ageDays };
          } catch { return { id, ageDays: 0 } as GmailRow; }
        }));
        setEmails(rows);
      })
      .catch((e) => setErrs((p) => ({ ...p, emails: String(e) })));
  }, [me]);

  // Load (and reload) the unified timeline whenever the calendar set changes.
  useEffect(() => { loadEvents(); }, [loadEvents]);

  // ── Per-account color (inline styles — avoids Tailwind purging dynamic class
  //    names). Only meaningful when more than one calendar is merged.
  const multiAccount = (cals?.length ?? 0) > 1;
  const acctColors = useMemo(() => {
    const palette = ['#38bdf8', '#a78bfa', '#34d399', '#fbbf24', '#f472b6', '#fb7185'];
    const m: Record<string, string> = {};
    (cals ?? []).forEach((c, i) => { m[c.subject] = palette[i % palette.length]; });
    return m;
  }, [cals]);
  const acctColor = useCallback(
    (subject?: string) => (subject && acctColors[subject]) || '#64748b',
    [acctColors],
  );

  // ── Derived KPIs
  const todaysEvents = useMemo(
    () => (events ?? []).filter((e) => e.start?.dateTime && isToday(new Date(e.start.dateTime!))),
    [events],
  );
  // All-day events carry start.date (a "YYYY-MM-DD" string), not start.dateTime,
  // so the hour-grid can't place them and previously dropped them entirely — that
  // was the "missing calendar events" report. Surface them in a strip above the grid.
  const allDayToday = useMemo(
    () => (events ?? []).filter((e) => isAllDayOn(e, now)),
    [events],
  );
  const unreadCount = emails?.length ?? 0;
  const agingUnread = (emails ?? []).filter((e) => e.ageDays >= 1);
  const dealsFollowup: any[] = hq?.deals_followup ?? [];

  // ── Alerts (severity-ranked, capped at 5)
  const alerts = useMemo<Alert[]>(() => {
    const out: Alert[] = [];
    for (const e of [...agingUnread].sort((a, b) => b.ageDays - a.ageDays).slice(0, 3)) {
      const sev: Severity = e.ageDays >= 3 ? 'stall' : 'watch';
      const who = cleanFrom(e.from);
      out.push({
        key: 'em-' + e.id,
        sev,
        ticker: tickerOf(who),
        eyebrow: `${who} · inbox`,
        headline: e.subject || '(no subject)',
        detail: `Unread ${Math.floor(e.ageDays)}d — ${e.snippet?.slice(0, 120) || 'no preview'}`,
        to: `/inbox/${e.id}`,
        primary: 'Open thread',
      });
    }
    for (const d of dealsFollowup.slice(0, 2)) {
      out.push({
        key: 'deal-' + d.id,
        sev: 'watch',
        ticker: tickerOf(d.name),
        eyebrow: `${d.name} · deal`,
        headline: `${d.name} has gone quiet`,
        detail: `Stage ${d.stage}. No activity logged recently — worth a touch before it cools.`,
        primary: 'Open deal',
      });
    }
    const nowMs = Date.now();
    const dm = loadDismiss();
    return out.filter((a) => !(dm[a.key] && dm[a.key] > nowMs)).slice(0, 5);
  }, [agingUnread, dealsFollowup, dismissTick]);

  function dismissAlert(key: string, untilMs: number, label: string) {
    const m = loadDismiss();
    m[key] = untilMs;
    saveDismiss(m);
    setDismissTick((t) => t + 1);
    toast(label);
  }

  // Inline task transition from the My Day Your Tasks card. Refetches the My Day
  // aggregate after each transition so counts + the card stay in sync without a
  // full page reload.
  const onTaskTransition = useCallback(async (id: string, status: string) => {
    if (!me) return;
    try {
      await transitionTask(id, status);
      const next = await getMyDay(me.userId);
      setHq(next);
      toast(status === 'done' ? '✓ Task completed' : status === 'in_progress' ? '▶ Started' : `Task → ${status}`);
    } catch (e) {
      toast(`Could not update task: ${e}`);
    }
  }, [me]);

  const myTasks: MyTask[] = (hq?.tasks_today ?? []).filter((t: MyTask) => t.status !== 'done' && t.status !== 'dropped');

  // Materialize today's Maker-schedule blocks (Available/transparent), trimmed
  // around real meetings + idempotent, then reload the grid.
  async function frameMyDay() {
    if (seeding || !subject) return;
    setSeeding(true);
    try {
      const r = await seedDayBlocks(subject, azToday());
      await loadEvents();
      const bits: string[] = [];
      if (r.created) bits.push(`${r.created} block${r.created === 1 ? '' : 's'} added`);
      if (r.trimmed) bits.push(`${r.trimmed} trimmed around a meeting`);
      if (r.skippedExisting) bits.push(`${r.skippedExisting} already set`);
      if (r.blockedOut) bits.push(`${r.blockedOut} fully booked`);
      toast(bits.length ? `Framed ${r.date} — ${bits.join(' · ')}` : 'Day already framed');
    } catch (e) {
      toast(`Couldn't frame day: ${String(e)}`);
    } finally {
      setSeeding(false);
    }
  }

  const scheduledHrs = useMemo(() => sumScheduledHours(todaysEvents), [todaysEvents]);
  const gapHrs = Math.max(0, GRID_END_H - GRID_START_H - scheduledHrs);

  // F8: one engine, two views. On phones render the single-column mobile layout
  // (full-width, self-scrolling, calendar collapsible) instead of the desktop
  // two-pane grid, which crushed the calendar into a sliver on a narrow viewport.
  // Same data + handlers; only the presentation forks.
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <MyDayMobileView
        me={me}
        now={now}
        myTasks={myTasks}
        alerts={alerts}
        events={events}
        errs={errs}
        todaysEvents={todaysEvents}
        allDayToday={allDayToday}
        multiAccount={multiAccount}
        acctColor={acctColor}
        scheduledHrs={scheduledHrs}
        gapHrs={gapHrs}
        unreadCount={unreadCount}
        dealsFollowupCount={dealsFollowup.length}
        voicePending={hq?.voice_pending?.length ?? 0}
        seeding={seeding}
        emailsLoaded={!!emails}
        emailsErr={errs.emails}
        onTaskTransition={onTaskTransition}
        onFrameDay={frameMyDay}
        onAlertOpen={(to) => nav(to)}
        onAlertSnooze={(key) => dismissAlert(key, Date.now() + 2 * 86_400_000, 'Snoozed 2 days')}
        onAlertResolve={(key) => dismissAlert(key, RESOLVED_UNTIL, 'Marked resolved')}
        onOpenAllTasks={() => nav('/pm')}
      />
    );
  }

  return (
    <div style={{ margin: '-32px -40px' }}>
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-10 py-7 max-w-[1400px] mx-auto">

          {/* Row 1 — breadcrumb + live badge */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">My Day</span>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-ok">
              <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse"></span>
              Live · synced just now
            </div>
          </div>

          {/* Row 2 — title block + KPI strip side-by-side */}
          <div className="flex items-start gap-6 mb-8">
            <div className="flex-shrink-0">
              <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">
                {me?.displayName ?? 'Vertis Group'} · {now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
              <h1 className="text-6xl font-bold tracking-tight text-paper-100 mb-2">My Day</h1>
              <p className="text-base text-muted whitespace-nowrap">
                {todaysEvents.length} meeting{todaysEvents.length === 1 ? '' : 's'} today · {unreadCount} unread · {dealsFollowup.length} to follow up{multiAccount ? ` · ${cals!.length} calendars` : ''}
              </p>
            </div>
            <div className="flex-1 grid grid-cols-4 gap-3">
              <Kpi label="Meetings today" value={todaysEvents.length} tone="accent-400" help={`${scheduledHrs.toFixed(1)}h scheduled`} zeroHelp="Open day" />
              <Kpi label="Unread inbox" value={unreadCount} tone="paper-100" help={`${agingUnread.length} aging`} />
              <Kpi label="To follow up" value={dealsFollowup.length} tone="watch" help="deals gone quiet" zeroHelp="All clear" />
              <Kpi label="Aging > 3d" value={agingUnread.filter((e) => e.ageDays >= 3).length} tone="stall" help="needs a reply" zeroHelp="All caught up" />
            </div>
          </div>

          {/* Daily Ops Review moved to its own route 2026-06-06.
              See routes/DailyOpsReview.tsx; My Day leads with Priorities + Calendar. */}

          {/* Row 3 — body grid. Priorities (tasks + alerts) on the LEFT; the
              "Today" calendar pinned TOP-RIGHT so it is always visible without
              scrolling past the task list. Previously Your Tasks was a
              full-width card above this grid, which pushed the calendar below
              the fold — the "calendar no longer showing up" report. */}
          <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr)' }}>

            {/* LEFT — Your Tasks + Alerts */}
            <div>
              {/* Your Tasks — open tasks for the signed-in operator (no due_date
                  floor so brand-new users see their onboarding tasks
                  immediately). Only renders when there is at least one open
                  task — an empty card would just be visual noise. */}
              {myTasks.length > 0 && (
                <TasksCard tasks={myTasks} onTransition={onTaskTransition} onOpenAll={() => nav('/pm')} />
              )}

              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-2xl font-bold text-paper-100">⚡ Alerts Needing Your Eye</h2>
                <span className="text-xs text-muted">
                  {alerts.length} active · tap any to drill in
                </span>
              </div>

              <div className="space-y-4">
                {!emails && !errs.emails && <Skeleton />}
                {errs.emails && <ErrCard msg={errs.emails} />}
                {emails && alerts.length === 0 && (
                  <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-6 text-center text-muted text-sm">
                    Nothing on fire. Inbox is current and no deals are stalling.
                  </div>
                )}
                {alerts.map((a) => (
                  <AlertCard
                    key={a.key}
                    a={a}
                    onOpen={(to) => nav(to)}
                    onSnooze={() => dismissAlert(a.key, Date.now() + 2 * 86_400_000, 'Snoozed 2 days')}
                    onResolve={() => dismissAlert(a.key, RESOLVED_UNTIL, 'Marked resolved')}
                  />
                ))}
              </div>

              {/* Quick Actions */}
              <div className="mt-7">
                <h3 className="text-sm uppercase tracking-[2px] text-muted font-semibold mb-3">Quick Actions</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Qa emoji="🗓" label={seeding ? 'Framing…' : 'Frame my day'} help="Fill gaps with Available blocks" onClick={frameMyDay} />
                  <Qa emoji="⚡" label="Capture" help="Voice, text, screenshot — triages later" />
                  <Qa emoji="＋" label="Log finding" help="Done or to-research; assign owner" />
                  <Qa emoji="✉" label="Send via Bridge" help="SA + DWD; pick from-domain" />
                  <Qa emoji="📅" label="Book meeting" help="via Bridge · AZ TZ" />
                  <Qa emoji="🔍" label="Run audit" help="Domain / books / website" />
                  <Qa emoji="🗂" label="Open CRM" help="Contacts, companies, deals" onClick={() => nav('/crm')} />
                </div>
              </div>
            </div>

            {/* RIGHT — hour-grid + capture */}
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-2xl font-bold text-paper-100">📅 Today</h2>
                <span className="text-xs text-muted">
                  {now.toLocaleDateString([], { month: 'short', day: 'numeric' })} · {scheduledHrs.toFixed(1)}h scheduled · {gapHrs.toFixed(1)}h gaps{allDayToday.length ? ` · ${allDayToday.length} all-day` : ''}
                </span>
              </div>

              {/* Account legend — one chip per unified calendar */}
              {multiAccount && cals && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
                  {cals.map((c) => (
                    <span key={c.subject} className="inline-flex items-center gap-1.5 text-[10px] text-muted">
                      <span style={{ width: 8, height: 8, borderRadius: 9999, background: acctColor(c.subject), display: 'inline-block' }} />
                      {c.label}
                    </span>
                  ))}
                </div>
              )}

              <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-4">
                {!events && !errs.events && <div className="text-muted text-xs py-6 text-center">Loading calendars…</div>}
                {errs.events && <div className="text-stall text-xs py-4">{errs.events}</div>}
                {events && allDayToday.length > 0 && (
                  <div className="mb-3 pb-3 border-b border-ink-800/60">
                    <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-1.5">All day</div>
                    <div className="flex flex-wrap gap-1.5">
                      {allDayToday.map((e, i) => (
                        <span key={`${e.id ?? e.summary ?? 'ad'}-${i}`} className="inline-flex items-center gap-1.5 text-xs text-paper-100 bg-ink-800/60 rounded px-2 py-1">
                          {multiAccount && <AcctDot color={acctColor(e.accountSubject)} />}
                          {e.summary || '(all day)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {events && <HourGrid events={todaysEvents} now={now} acctColor={acctColor} multiAccount={multiAccount} />}
              </div>

              {/* Capture box — always anchored bottom-right */}
              <div className="mt-4 rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 text-ink-950 p-5">
                <div className="text-[11px] uppercase tracking-[2px] opacity-70 font-semibold mb-2">Capture</div>
                <button className="w-full bg-white text-accent-600 rounded-md py-2.5 font-semibold flex items-center justify-center gap-2">
                  <span>🎙</span><span>Voice + text + screenshot</span><kbd className="text-[10px] opacity-60">⌘⇧Space</kbd>
                </button>
                <div className="mt-3 text-[11px] opacity-80">
                  {hq?.voice_pending?.length ?? 0} captures pending · <span className="underline cursor-pointer">Review</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

// ── Your Tasks card (V14): tight V14 surface for the operator's open tasks.
// Sits above the body grid so a brand-new user (no calendar, no inbox yet) has
// an obvious finish line on landing. Priority badges + inline Start/Done so they
// can transition without leaving the page. Caps at 8 rows — anything past that
// is what the full /pm route is for.
function TasksCard({ tasks, onTransition, onOpenAll }: { tasks: MyTask[]; onTransition: (id: string, status: string) => void; onOpenAll: () => void }) {
  const top = tasks.slice(0, 8);
  const more = tasks.length - top.length;
  return (
    <div className="mb-8 rounded-xl border border-ink-800 bg-gradient-to-br from-ink-900 to-ink-900/40 p-6 ring-1 ring-accent-500/10">
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-baseline gap-3">
          <h2 className="text-2xl font-bold text-paper-100">📋 Your Tasks</h2>
          <span className="text-xs text-muted">{tasks.length} open</span>
        </div>
        <button onClick={onOpenAll} className="text-[11px] text-accent-400 hover:text-accent-300 font-semibold">
          Open all →
        </button>
      </div>
      <div className="space-y-2">
        {top.map((t) => (
          <TaskRow key={t.id} task={t} onTransition={onTransition} />
        ))}
      </div>
      {more > 0 && (
        <div className="text-[11px] text-muted mt-3 text-center">
          and {more} more — <button className="underline" onClick={onOpenAll}>see all</button>
        </div>
      )}
    </div>
  );
}

function TaskRow({ task, onTransition }: { task: MyTask; onTransition: (id: string, status: string) => void }) {
  const tone = task.priority === 1 ? 'stall' : task.priority === 2 ? 'watch' : 'muted';
  const isStarted = task.status === 'in_progress';
  return (
    <div className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-ink-800/40 hover:bg-ink-800/70 transition">
      <span className={`text-[9px] uppercase tracking-[1px] font-bold px-1.5 py-0.5 rounded bg-${tone}/15 text-${tone} flex-shrink-0 mt-0.5`}>
        P{task.priority}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-paper-100 leading-snug">
          {isStarted && <span className="text-accent-400 mr-1.5">▶</span>}
          {task.title}
        </div>
        {task.body && <div className="text-xs text-muted mt-0.5 line-clamp-2">{task.body}</div>}
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        {task.status === 'todo' && (
          <button
            onClick={() => onTransition(task.id, 'in_progress')}
            className="text-[11px] bg-ink-700 hover:bg-ink-600 text-paper-100 px-2 py-1 rounded"
          >
            Start
          </button>
        )}
        <button
          onClick={() => onTransition(task.id, 'done')}
          className="text-[11px] bg-ok/15 hover:bg-ok/25 text-ok px-2 py-1 rounded"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── KPI card. value===0 is good news, not an alarm: render muted + a positive
//    zeroHelp (falls back to help) instead of the alarm tone.
function Kpi({ label, value, tone, help, zeroHelp }: { label: string; value: number; tone: string; help: string; zeroHelp?: string }) {
  const isZero = value === 0;
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">{label}</div>
      <div className={`text-4xl font-bold tabular-nums mt-1 text-${isZero ? 'muted' : tone}`}>{value}</div>
      <div className="text-[11px] text-muted mt-0.5">{isZero ? (zeroHelp ?? help) : help}</div>
    </div>
  );
}

// ── Alert card (V14 §4)
function AlertCard({ a, onOpen, onSnooze, onResolve }: { a: Alert; onOpen: (to: string) => void; onSnooze: () => void; onResolve: () => void }) {
  return (
    <div className={`rounded-xl border-2 border-${a.sev}/40 border-l-4 border-l-${a.sev} bg-gradient-to-br from-ink-900 to-ink-900/40 p-5 ring-1 ring-${a.sev}/10`}>
      <div className="flex items-start gap-4">
        <div className={`w-9 h-9 rounded-lg bg-${a.sev}/15 text-${a.sev} flex items-center justify-center text-sm font-bold flex-shrink-0`}>
          {a.ticker}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] uppercase tracking-[2px] text-${a.sev} font-bold`}>{a.eyebrow}</span>
            <span className={`text-[9px] uppercase tracking-[1px] font-bold px-1.5 py-0.5 rounded bg-${a.sev}/15 text-${a.sev}`}>{sevLabel(a.sev)}</span>
          </div>
          <div className="text-lg font-semibold text-paper-100 leading-snug">{a.headline}</div>
          <div className="text-sm text-muted mt-1">{a.detail}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={() => a.to && onOpen(a.to)}
              className="bg-accent-500 hover:bg-accent-600 text-ink-950 text-xs px-3 py-1.5 rounded font-semibold"
            >
              {a.primary}
            </button>
            <button onClick={onSnooze} className="bg-ink-800 hover:bg-ink-700 text-paper-100 text-xs px-3 py-1.5 rounded">Snooze 2d</button>
            <button onClick={onResolve} className="text-xs text-muted hover:text-paper-100 px-2 py-1.5">Mark resolved</button>
          </div>
        </div>
        <span className="text-2xl text-muted/40">›</span>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-5 animate-pulse">
      <div className="h-4 w-40 bg-ink-800 rounded mb-3"></div>
      <div className="h-3 w-full bg-ink-800/60 rounded"></div>
    </div>
  );
}
function ErrCard({ msg }: { msg: string }) {
  return <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 text-stall text-xs ring-1 ring-stall/10">{msg}</div>;
}

// ── Quick-action tile
function Qa({ emoji, label, help, onClick }: { emoji: string; label: string; help: string; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="bg-ink-900/60 border border-ink-800 hover:border-accent-500/50 rounded-lg p-4 text-left transition">
      <div className="text-2xl mb-1">{emoji}</div>
      <div className="text-sm font-semibold text-paper-100">{label}</div>
      <div className="text-[11px] text-muted mt-0.5">{help}</div>
    </button>
  );
}

// ── Account color dot for the unified calendar view (inline-styled)
function AcctDot({ color, dim }: { color: string; dim?: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block', width: 6, height: 6, borderRadius: 9999,
        background: color, marginRight: 6, verticalAlign: 'middle', opacity: dim ? 0.6 : 1,
      }}
    />
  );
}

// ── Hour-grid (V14 §7) — half-hour rows, gaps visible, NOW marker
function HourGrid({ events, now, acctColor, multiAccount }: { events: CalEvent[]; now: Date; acctColor: (subject?: string) => string; multiAccount: boolean }) {
  const rows: { h: number; m: number }[] = [];
  for (let h = GRID_START_H; h < GRID_END_H; h++) {
    rows.push({ h, m: 0 });
    rows.push({ h, m: 30 });
  }
  const nowMin = now.getHours() * 60 + now.getMinutes();

  return (
    <div>
      {rows.map(({ h, m }) => {
        const slotStart = h * 60 + m;
        const slotEnd = slotStart + 30;
        const covering = events.filter((e) => {
          if (!e.start?.dateTime) return false;
          const s = new Date(e.start.dateTime);
          const en = e.end?.dateTime ? new Date(e.end.dateTime) : new Date(s.getTime() + 30 * 60000);
          const sMin = s.getHours() * 60 + s.getMinutes();
          const eMin = en.getHours() * 60 + en.getMinutes();
          return sMin < slotEnd && eMin > slotStart;
        });
        // A real (opaque) meeting wins the slot over an Available framed block,
        // so an overlapping meeting always renders on top.
        const ev = covering.find((e) => !isFramedBlock(e)) ?? covering[0] ?? null;
        const framed = ev ? isFramedBlock(ev) : false;
        const isNow = nowMin >= slotStart && nowMin < slotEnd;
        const isEventStart = ev && (() => {
          const s = new Date(ev.start!.dateTime!);
          return s.getHours() === h && (m === 0 ? s.getMinutes() < 30 : s.getMinutes() >= 30);
        })();
        const tone = ev ? eventTone(ev) : null;

        return (
          <div key={`${h}:${m}`} className={`flex items-center py-1 border-t border-ink-800/40 ${isNow ? 'bg-stall/5' : ''}`}>
            <div className={`w-16 text-[10px] flex-shrink-0 ${isNow ? 'text-stall font-semibold' : 'text-muted'}`}>
              {fmtSlot(h, m)}
            </div>
            <div className="flex-1 min-w-0">
              {ev ? (
                isEventStart || isNow ? (
                  framed ? (
                    <div className="ml-2 px-3 py-1 rounded border-l-2 border-dashed border-accent-500/40 bg-accent-500/5 text-paper-100/60 text-xs truncate"
                         style={multiAccount ? { borderLeftColor: acctColor(ev.accountSubject) } : undefined}>
                      {multiAccount && <AcctDot color={acctColor(ev.accountSubject)} dim />}
                      {ev.summary || 'Available'} <span className="text-muted/60">· free</span>
                    </div>
                  ) : (
                    <div className={`ml-2 px-3 py-1 rounded bg-${tone}/15 border-l-2 border-${tone} text-paper-100 text-xs truncate ${isNow ? 'ring-1 ring-stall/40' : ''}`}
                         style={multiAccount ? { borderLeftColor: acctColor(ev.accountSubject) } : undefined}>
                      {isNow && <span className="text-stall font-semibold">NOW · </span>}
                      {multiAccount && <AcctDot color={acctColor(ev.accountSubject)} />}
                      {ev.summary || '(busy)'}
                      {ev.attendees && ev.attendees.length > 1 && <span className="text-muted"> · {ev.attendees.length}</span>}
                    </div>
                  )
                ) : (
                  <div className={`ml-2 h-4 border-l-2 ${framed ? 'border-accent-500/25 border-dashed' : `border-${tone}/50`}`}
                       style={multiAccount ? { borderLeftColor: acctColor(ev.accountSubject) } : undefined}></div>
                )
              ) : (
                <div className="ml-2 text-muted/30 text-[10px] italic">— gap</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Mobile view (F8) — single column for the phone. Same data + handlers as the
// desktop two-pane; only the presentation forks. Owns its own scroll (the v5
// canvas is overflow:hidden) and uses no negative margins, so nothing gets
// crushed or clipped on a narrow viewport. Calendar is full-width + collapsible.
type MyDayMobileViewProps = {
  me: ReturnType<typeof useMeUser>;
  now: Date;
  myTasks: MyTask[];
  alerts: Alert[];
  events: CalEvent[] | null;
  errs: Record<string, string>;
  todaysEvents: CalEvent[];
  allDayToday: CalEvent[];
  multiAccount: boolean;
  acctColor: (subject?: string) => string;
  scheduledHrs: number;
  gapHrs: number;
  unreadCount: number;
  dealsFollowupCount: number;
  voicePending: number;
  seeding: boolean;
  emailsLoaded: boolean;
  emailsErr?: string;
  onTaskTransition: (id: string, status: string) => void;
  onFrameDay: () => void;
  onAlertOpen: (to: string) => void;
  onAlertSnooze: (key: string) => void;
  onAlertResolve: (key: string) => void;
  onOpenAllTasks: () => void;
};

function MyDayMobileView(p: MyDayMobileViewProps) {
  const [calOpen, setCalOpen] = useState(false);
  const eventCount = p.todaysEvents.length + p.allDayToday.length;
  return (
    <div className="bg-ink-950 text-paper-100 font-sans h-full overflow-y-auto px-4 py-5">
      {/* Header — compact */}
      <div className="flex items-center justify-between text-[11px] mb-3">
        <span className="text-muted">{p.me?.displayName ?? 'Vertis Group'}</span>
        <span className="flex items-center gap-1.5 text-ok"><span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse" />Live</span>
      </div>
      <div className="text-[10px] uppercase tracking-[2px] text-muted mb-1">
        {p.now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
      </div>
      <h1 className="text-3xl font-bold tracking-tight mb-2">My Day</h1>

      {/* Day shape (F1) — what the day actually looks like, in one line. */}
      <p className="text-sm text-muted mb-5">
        {p.todaysEvents.length} mtg{p.todaysEvents.length === 1 ? '' : 's'} · {p.scheduledHrs.toFixed(1)}h booked · {p.gapHrs.toFixed(1)}h free · {p.unreadCount} unread · {p.dealsFollowupCount} to follow up
      </p>

      {/* Calendar — full-width collapsible so it is never crushed. */}
      <button
        onClick={() => setCalOpen((o) => !o)}
        className="w-full flex items-center justify-between rounded-xl border border-ink-800 bg-ink-900/60 px-4 py-3 mb-3"
      >
        <span className="text-sm font-semibold">📅 Today</span>
        <span className="text-xs text-muted">{eventCount} event{eventCount === 1 ? '' : 's'} · {calOpen ? 'hide' : 'show'}</span>
      </button>
      {calOpen && (
        <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-3 mb-5">
          {!p.events && !p.errs.events && <div className="text-muted text-xs py-4 text-center">Loading calendars…</div>}
          {p.errs.events && <div className="text-stall text-xs py-2">{p.errs.events}</div>}
          {p.events && p.allDayToday.length > 0 && (
            <div className="mb-3 pb-3 border-b border-ink-800/60">
              <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-1.5">All day</div>
              <div className="flex flex-wrap gap-1.5">
                {p.allDayToday.map((e, i) => (
                  <span key={`${e.id ?? e.summary ?? 'ad'}-${i}`} className="inline-flex items-center gap-1.5 text-xs text-paper-100 bg-ink-800/60 rounded px-2 py-1">
                    {p.multiAccount && <AcctDot color={p.acctColor(e.accountSubject)} />}
                    {e.summary || '(all day)'}
                  </span>
                ))}
              </div>
            </div>
          )}
          {p.events && <HourGrid events={p.todaysEvents} now={p.now} acctColor={p.acctColor} multiAccount={p.multiAccount} />}
        </div>
      )}

      {/* Numbered focus list — the ranked order (my_day_rank once the API ships it,
          else display order). Each card is self-contained: act without a desk. */}
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-lg font-bold">Today, in order</h2>
        {p.myTasks.length > 0 && <button onClick={p.onOpenAllTasks} className="text-[11px] text-accent-400 font-semibold">All →</button>}
      </div>
      {p.myTasks.length === 0 ? (
        <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-5 text-center text-muted text-sm mb-6">No open tasks.</div>
      ) : (
        <div className="space-y-2 mb-6">
          {p.myTasks.map((t, i) => (
            <div key={t.id} className="flex items-start gap-3 rounded-xl border border-ink-800 bg-ink-900/60 px-3 py-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-accent-500/15 text-accent-400 text-sm font-bold flex items-center justify-center mt-0.5">
                {t.my_day_rank ?? i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-medium leading-snug">
                  {t.status === 'in_progress' && <span className="text-accent-400 mr-1">▶</span>}
                  {t.title}
                </div>
                {t.body && <div className="text-xs text-muted mt-1 line-clamp-2">{t.body}</div>}
                <div className="flex gap-2 mt-2">
                  {t.status === 'todo' && (
                    <button onClick={() => p.onTaskTransition(t.id, 'in_progress')} className="text-xs bg-ink-700 hover:bg-ink-600 px-3 py-1.5 rounded">Start</button>
                  )}
                  <button onClick={() => p.onTaskTransition(t.id, 'done')} className="text-xs bg-ok/15 text-ok px-3 py-1.5 rounded">Done</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Alerts */}
      <h2 className="text-lg font-bold mb-2">⚡ Needs your eye</h2>
      <div className="space-y-3 mb-6">
        {!p.emailsLoaded && !p.emailsErr && <Skeleton />}
        {p.emailsErr && <ErrCard msg={p.emailsErr} />}
        {p.emailsLoaded && p.alerts.length === 0 && (
          <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-5 text-center text-muted text-sm">Nothing on fire.</div>
        )}
        {p.alerts.map((a) => (
          <AlertCard
            key={a.key}
            a={a}
            onOpen={p.onAlertOpen}
            onSnooze={() => p.onAlertSnooze(a.key)}
            onResolve={() => p.onAlertResolve(a.key)}
          />
        ))}
      </div>

      {/* Frame the day + capture */}
      <button
        onClick={p.onFrameDay}
        disabled={p.seeding}
        className="w-full rounded-xl border border-ink-800 bg-ink-900/60 py-3 text-sm font-semibold mb-3 disabled:opacity-60"
      >
        🗓 {p.seeding ? 'Framing…' : 'Frame my day'}
      </button>
      <div className="rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 text-ink-950 p-4">
        <div className="text-[11px] uppercase tracking-[2px] opacity-70 font-semibold mb-2">Capture</div>
        <button className="w-full bg-white text-accent-600 rounded-md py-2.5 font-semibold flex items-center justify-center gap-2">
          <span>🎙</span><span>Voice + text + screenshot</span>
        </button>
        <div className="mt-2 text-[11px] opacity-80">{p.voicePending} captures pending</div>
      </div>
    </div>
  );
}

// ── helpers
function isToday(d: Date) {
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}
// Local "YYYY-MM-DD" for comparing against Google all-day date strings.
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// True when an all-day event (start.date, end.date exclusive) covers `day`.
// Timed events (start.dateTime) return false — they belong in the hour-grid.
function isAllDayOn(e: CalEvent, day: Date): boolean {
  const s = e.start?.date;
  if (!s) return false;
  const t = ymd(day);
  const end = e.end?.date;
  return s <= t && (!end || t < end);
}
function fmtSlot(h: number, m: number) {
  const ap = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${m === 0 ? '00' : '30'} ${ap}`;
}
function eventTone(e: CalEvent): 'accent-500' | 'blue-400' | 'watch' {
  const s = (e.summary || '').toLowerCase();
  if (e.attendees && e.attendees.length > 1) return 'blue-400';
  if (/focus|deep work|build|code|block|design|write/.test(s)) return 'accent-500';
  return 'watch';
}
function sumScheduledHours(events: CalEvent[]): number {
  let mins = 0;
  for (const e of events) {
    if (!e.start?.dateTime || !e.end?.dateTime) continue;
    const s = new Date(e.start.dateTime); const en = new Date(e.end.dateTime);
    const sMin = Math.max(GRID_START_H * 60, s.getHours() * 60 + s.getMinutes());
    const eMin = Math.min(GRID_END_H * 60, en.getHours() * 60 + en.getMinutes());
    if (eMin > sMin) mins += eMin - sMin;
  }
  return mins / 60;
}
function cleanFrom(from?: string): string {
  if (!from) return 'Unknown';
  const m = from.match(/^"?([^"<]+?)"?\s*</);
  if (m) return m[1].trim();
  const e = from.match(/<?([^<>@\s]+@[^<>\s]+)>?/);
  return e ? e[1] : from;
}
function tickerOf(name: string): string {
  const parts = name.replace(/[^A-Za-z ]/g, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  return '··';
}
function sevLabel(sev: Severity): string {
  return sev === 'stall' ? 'STALLED' : sev === 'watch' ? 'WATCH' : 'OK';
}
